import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import { dateDifference } from 'lib/datetime/dateUtils'

export function buildCruiseInclusionView(inclusion: Cruises.OfferInclusion, amount: number, regionCode: string): Array<Cruises.InclusionItemView> {
  const inclusionItemViewList: Array<Cruises.InclusionItemView> = []

  inclusion.items.forEach((item) => {
    const basicInclusionItem = {
      id: item.id,
      inclusionType: inclusion.type,
      startDate: inclusion.startDate,
      endDate: inclusion.endDate,
      type: item.type,
    }

    if (item.type === 'ONBOARD_CREDIT') {
      const percentageAmount = item.creditPercentage ? Math.round(amount / item.creditPercentage) : 0
      let totalCreditAmount = percentageAmount || item.creditAmount

      // Sum credit amount and percentage amount if both are present
      if (percentageAmount && item.creditAmount) {
        totalCreditAmount = item.creditAmount + percentageAmount
      }

      if (totalCreditAmount && item.creditCurrency) {
        inclusionItemViewList.push({
          ...basicInclusionItem,
          totalCreditAmount,
          formattedTotalAmount: formatCurrency(totalCreditAmount, item.creditCurrency, regionCode)!,
          creditCurrency: item.creditCurrency,
        })
      }
    }

    if (item.description) {
      inclusionItemViewList.push({
        ...basicInclusionItem,
        description: item.description,
      })
    }
  })

  return inclusionItemViewList
}

export function buildCruiseInclusionDetails(inclusionItems: Array<Cruises.InclusionItemView>) {
  const hasLuxExclusive = inclusionItems.some(({ inclusionType }) => inclusionType === 'LUX_EXCLUSIVE')
  const hasNewInclusion = inclusionItems.some(({ startDate }) => dateDifference(new Date(), new Date(startDate)).days < 7)
  const endingInInclusion = Math.min(
    ...inclusionItems.map(({ endDate }) => dateDifference(new Date(endDate), new Date()).days),
  )

  return {
    hasLuxExclusive,
    hasNewInclusion,
    endingInInclusion,
  }
}

export function buildEvergreenPackageInclusions(
  evergreenInclusions: Array<App.EvergreenInclusion>,
): Array<App.PackageInclusion> {
  return evergreenInclusions.map((inclusion) => ({
    id: inclusion.id,
    description: inclusion.description,
    symbol: 'key',
    isHighlighted: true,
  }))
}

export function buildLegacyPackageInclusions(
  promotionDetails: App.CruisePromotionDetails | null,
  regionCode: string,
): Array<App.PackageInclusion> {
  const inclusions: Array<App.PackageInclusion> = []

  if (promotionDetails?.onBoardCredit) {
    const value = formatCurrency(promotionDetails.onBoardCredit.amount, promotionDetails.onBoardCredit.currencyCode, regionCode)
    inclusions.push({
      id: 'onBoardCredit',
      description: `Up to ${value} onboard credit available`,
    })
  }

  if (promotionDetails?.sellingPoints) {
    promotionDetails.sellingPoints.forEach((point) => {
      inclusions.push({ id: point, description: point })
    })
  }

  return inclusions
}

export function buildPackageInclusions(
  inclusions: Array<Cruises.InclusionItemView>,
): Array<App.PackageInclusion> {
  return inclusions.map(({ id, type, formattedTotalAmount, description }) => {
    const isOnboardCredit = type === 'ONBOARD_CREDIT'
    const inclusionDescription = isOnboardCredit ? `${formattedTotalAmount} Onboard Credit per cabin` : description

    return {
      id,
      description: inclusionDescription!,
      symbol: isOnboardCredit ? 'dollar_sign_in_circle' : undefined,
    }
  })
}
